/**
 * Pure, dependency-free helpers for the recording page's "skip inactivity"
 * playback and seek-bar marks.
 *
 * Every interaction carries a `relativeTimestamp` (ms from recording start) that
 * aligns 1:1 with the video's playback time (see interactionLogsTab.tsx, which
 * compares it directly against the playhead). From that we derive the "active"
 * windows of the recording (a padded window around each interaction) and their
 * complement (the inactive gaps to skip / shade).
 */

export interface ActiveWindow {
  start: number; // ms
  end: number; // ms
}

/** Video to keep around each interaction (ms): 1s before and 1s after. */
export const PAD_BEFORE_MS = 1000;
export const PAD_AFTER_MS = 1000;

/**
 * Minimum distance the playhead must be behind the next active moment before we
 * auto-seek. Avoids jitter / seek loops from `timeupdate` firing repeatedly.
 */
export const SKIP_JITTER_MS = 250;

const clamp = (value: number, min: number, max: number) =>
  Math.min(Math.max(value, min), max);

/**
 * Build the merged active windows for a recording. Each interaction contributes
 * a `[t - pad, t + pad]` window (clamped to the video); windows that touch or
 * overlap are merged into one continuous active segment so closely-spaced
 * interactions don't produce micro-skips.
 *
 * Returns windows sorted by `start`, non-overlapping. Empty when there is no
 * duration yet or no interactions.
 */
export function computeActiveWindows(
  interactionLogs: Array<{ relativeTimestamp: number }>,
  durationMs: number,
  padBeforeMs: number = PAD_BEFORE_MS,
  padAfterMs: number = PAD_AFTER_MS
): ActiveWindow[] {
  if (durationMs <= 0 || interactionLogs.length === 0) return [];

  const windows = interactionLogs
    .map((log) => log.relativeTimestamp)
    .filter((t) => Number.isFinite(t))
    .sort((a, b) => a - b)
    .map((t) => ({
      start: clamp(t - padBeforeMs, 0, durationMs),
      end: clamp(t + padAfterMs, 0, durationMs),
    }));

  const merged: ActiveWindow[] = [];
  for (const w of windows) {
    const last = merged[merged.length - 1];
    if (last && w.start <= last.end) {
      last.end = Math.max(last.end, w.end);
    } else {
      merged.push({ ...w });
    }
  }
  return merged;
}

/**
 * The complement of the active windows: the dead-time stretches that get
 * skipped during playback and shaded on the seek bar. Includes the leading gap
 * before the first window and the trailing gap after the last one.
 */
export function inactiveGaps(
  windows: ActiveWindow[],
  durationMs: number
): ActiveWindow[] {
  if (durationMs <= 0) return [];
  if (windows.length === 0) return [{ start: 0, end: durationMs }];

  const gaps: ActiveWindow[] = [];
  let cursor = 0;
  for (const w of windows) {
    if (w.start > cursor) gaps.push({ start: cursor, end: w.start });
    cursor = Math.max(cursor, w.end);
  }
  if (cursor < durationMs) gaps.push({ start: cursor, end: durationMs });
  return gaps;
}

/**
 * Given the current playhead position, return the time we should jump to in
 * order to skip inactivity, or `null` when no skip is needed:
 *  - inside a gap before some window -> the start of that next window
 *  - inside an active window         -> null (let it play)
 *  - past the last window            -> durationMs (skip trailing dead time)
 */
export function nextActiveTime(
  windows: ActiveWindow[],
  tMs: number,
  durationMs: number
): number | null {
  if (windows.length === 0) return null;

  for (const w of windows) {
    if (tMs < w.start) return w.start; // currently in a gap before this window
    if (tMs <= w.end) return null; // inside this active window
  }
  return durationMs; // past the last window -> trailing dead time
}
